import { React, useEffect, useState } from "react";
import { Box, Link } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";

import { gcpResourceConfigs } from "./gcpResourceDefintions";
import GCPResourceButtonGroup from "./GCPResourceButtonGroup";
import DisplayDetails from "./common/DisplayDetails";
import LinearProgress from "@mui/material/LinearProgress";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

// This allows us to use sx={} instead of className={} for the img tag
const Img = styled("img")``;

export default function GCPResource({
  node,
  refreshNodes,
  sendNodeUpstream,
  removeNode,
  environmentId,
  organization,
  globalVariables,
}) {
  const [moduleStatus, setModuleStatus] = useState({
    status: "UININTIALIZED",
    deployed: false,
  });
  const [moduleUrl, setModuleUrl] = useState(null);
  const [nodeName, setNodeName] = useState("");
  const theme = useTheme();
  const [nodeData, setNodeData] = useState({
    id: 1,
    type: "organization",
    properties: {},
    parent: null,
    children: [],
    module_version: "",
  }); // Must initialize to an object to avoid errors
  const [parentStatus, setParentStatus] = useState(null);

  useEffect(() => {
    let unsubscribe_node = null;
    let unsubscribe_parent = null;
    unsubscribe_node = onSnapshot(
      doc(
        db,
        `organizations/${organization.id}/environments/${environmentId}/modules`,
        node.id
      ),
      (doc) => {
        const node_data = doc.data();
        if (node_data === undefined) {
          // Node data is undefined probably the node is deleted
          return;
        }
        setNodeData(node_data);
        setModuleStatus({
          status: node_data?.status ? node_data.status : "UNINITIALIZED",
          deployed: node_data?.deployed ? node_data.deployed : false,
        });
      }
    );
    if (node.type !== "organization") {
      unsubscribe_parent = onSnapshot(
        doc(
          db,
          `organizations/${organization.id}/environments/${environmentId}/modules`,
          node.parent.id
        ),
        (doc) => {
          const node_data = doc.data();
          if (node_data === undefined) {
            // Node data is undefined probably the node is deleted
            return;
          }
          setParentStatus({
            status: node_data?.status ? node_data.status : "UNINITIALIZED",
            deployed: node_data?.deployed ? node_data.deployed : false,
          });
        }
      );
    }
    if ("url" in gcpResourceConfigs[node.type]) {
      setModuleUrl(gcpResourceConfigs[node.type].url);
    } else {
      setModuleUrl(null);
    }
    return () => {
      if (unsubscribe_node !== null) {
        unsubscribe_node();
      }
      if (unsubscribe_parent !== null) {
        unsubscribe_parent();
      }
    };
    // eslint-disable-next-line
  }, [node]);

  useEffect(() => {
    updateNodeName();
    // eslint-disable-next-line
  }, [nodeData, globalVariables]);

  const updateNodeName = () => {
    let field = gcpResourceConfigs[nodeData.type]?.displayField;
    if (nodeData?.properties) {
      if (field in nodeData.properties) {
        let value = nodeData.properties[field];
        if (value.startsWith("$_")) {
          if (!globalVariables) {
            setNodeName(value);
            return;
          }
          const key = value.slice(2);
          const globalValue = globalVariables[key];
          if (globalValue !== undefined) {
            // Use the global value
            return setNodeName(globalValue);
          } else {
            // Handle the case where the global value is undefined
            setNodeName(value);
            return;
          }
        }
        return setNodeName(value);
      }
    }
  };

  const isNodeDataSource = (node_type) => {
    const { isDataSource } = gcpResourceConfigs[node_type];
    if (isDataSource) {
      return isDataSource;
    }
    return false;
  };

  const setBackgroundImage = (node, status) => {
    if (isNodeDataSource(node.type)) {
      return theme.palette.mode === "dark"
        ? "linear-gradient(to bottom right, lightblue, black)"
        : "linear-gradient(to bottom right, lightblue, white)";
    }
    if (
      status.includes("SUCCESS") ||
      status.includes("WORKING") ||
      status.includes("QUEUED")
    ) {
      return theme.palette.mode === "dark"
        ? "linear-gradient(to bottom right, green, black)"
        : "linear-gradient(to bottom right, lightgreen, white)";
    }
    if (status.includes("FAILURE")) {
      return theme.palette.mode === "dark"
        ? "linear-gradient(to bottom right, coral, black)"
        : "linear-gradient(to bottom right, lightcoral, white)";
    }
    return null;
  };

  const linearProgress = () => (
    <Box sx={{ ml: 1, mr: 1, mb: 1 }}>
      <LinearProgress />
    </Box>
  );

  const showProgress = () => {
    return (
      moduleStatus.status.includes("WORKING") ||
      moduleStatus.status.includes("QUEUED") ||
      moduleStatus.status.includes("PROGRESS")
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          border: 1,
          borderColor: "grey.500",
          ml: 1,
          mr: 1,
          mt: 1,
          mb: (theme) =>
            moduleStatus.status.includes("WORKING") ||
            moduleStatus.status.includes("PROGRESS") ||
            moduleStatus.status.includes("QUEUED")
              ? 0
              : 1,
          backgroundImage: (theme) =>
            setBackgroundImage(node, moduleStatus.status),
          //height: "20",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            width: "25px",
            height: "25px",
            margin: 1,
          }}
        >
          <Img
            src={gcpResourceConfigs[node.type].icon}
            alt="resource icon"
            data-allow-toggle
            sx={{ display: "flex", width: "100%", height: "100%" }}
          ></Img>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexGrow: 1,
            }}
          >
            {node.type !== "organization" && (
              <DisplayDetails
                componentName="Module Type:"
                componentValue={gcpResourceConfigs[node.type].displayName}
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type !== "organization" &&
              node.type !== "project_source" &&
              node.type !== "folder_source" && (
                <DisplayDetails
                  componentName="Name:"
                  componentValue={nodeName}
                  bottomMargin={moduleUrl ? false : true}
                />
              )}
            {!isNodeDataSource(node.type) && (
              <DisplayDetails
                componentName="Version:"
                componentValue={
                  nodeData?.module_version
                    ? nodeData.module_version
                    : gcpResourceConfigs[node.type].versions[0]
                }
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {!isNodeDataSource(node.type) && (
              <DisplayDetails
                componentName="Deployment Status:"
                componentValue={moduleStatus.status}
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type === "organization" && (
              <DisplayDetails
                componentName="Name:"
                componentValue="Root of the Architecture"
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type === "organization" && (
              <DisplayDetails
                componentName="Global Variables:"
                componentValue="Set Global Variables at this level"
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type === "project_source" && (
              <DisplayDetails
                componentName="Project ID:"
                componentValue={nodeName}
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type === "project_source" && (
              <DisplayDetails
                componentName="Info:"
                componentValue="Already deployed project"
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type === "folder_source" && (
              <DisplayDetails
                componentName="Folder ID:"
                componentValue={nodeName}
                bottomMargin={moduleUrl ? false : true}
              />
            )}
            {node.type === "folder_source" && (
              <DisplayDetails
                componentName="Info:"
                componentValue="Already created folder"
                bottomMargin={moduleUrl ? false : true}
              />
            )}
          </Box>
          {moduleUrl && (
            <Box
              alignItems="center"
              justifyContent="left"
              ml={1}
              display="flex"
            >
              <Link
                href={moduleUrl}
                variant="caption"
                fontSize={9}
                underline="hover"
                rel="noreferrer"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(moduleUrl, "_blank");
                }}
              >
                {moduleUrl}
              </Link>
            </Box>
          )}
        </Box>
        <GCPResourceButtonGroup
          key={node.id}
          node={nodeData}
          refreshNodes={refreshNodes}
          sendNodeUpstream={sendNodeUpstream}
          removeNode={removeNode}
          environmentId={environmentId}
          organizationId={organization.id}
          status={moduleStatus}
          parentStatus={parentStatus}
        />
      </Box>
      {showProgress() ? linearProgress() : <></>}
    </Box>
  );
}
